import { useEffect, useMemo, useRef } from "react";
import type { Book, Manual, Store } from "@tebiki/shared";
import { BookCover, type BookCoverModel } from "./BookCover.js";
import { readPct, isComplete } from "../lib/readingProgress.js";

export type ShelfDensity = "cover" | "spine";
export type ShelfSort = "recent" | "name" | "count";

interface BookshelfProps {
  books: Book[];
  /** all manuals (for per-book progress / status badges) */
  manuals: Manual[];
  stores: Store[];
  density: ShelfDensity;
  sort: ShelfSort;
}

const ALL_STORES = "全店共通";

/** Build the cover model for one real Book from its contained manuals. */
export function coverModel(book: Book, manualById: Map<string, Manual>, subtitle: string): BookCoverModel {
  const contents = book.manualIds.map((id) => manualById.get(id)).filter((m): m is Manual => !!m);
  const pct = contents.length
    ? Math.round(contents.reduce((s, m) => s + readPct(m.manualId), 0) / contents.length)
    : 0;
  return {
    key: book.bookId,
    title: book.title,
    subtitle,
    count: contents.length,
    pct,
    done: contents.length > 0 && contents.every((m) => isComplete(m.manualId)),
    mustRead: book.mustRead,
    processing: contents.some((m) => m.status === "processing"),
    ribbon: contents.some((m) => m.status === "draft" || m.status === "review"),
  };
}

/**
 * The 書架: REAL Book entities laid out as face-out covers on horizontally-scrollable shelves.
 * Shelves group by target store (a book targeting several stores sits on each of their shelves;
 * empty audience → the 全店共通 shelf). Pure renderer — Library owns loading/filtering/creation.
 */
export function Bookshelf({ books, manuals, stores, density, sort }: BookshelfProps) {
  const manualById = useMemo(() => new Map(manuals.map((m) => [m.manualId, m])), [manuals]);
  const storeName = useMemo(() => {
    const m = new Map(stores.map((s) => [s.id, s.name]));
    return (id: string) => m.get(id) ?? "不明な店舗";
  }, [stores]);

  const shelves = useMemo(() => {
    const groups = new Map<string, { id: string; label: string; books: Book[] }>();
    const put = (id: string, label: string, b: Book) => {
      let g = groups.get(id);
      if (!g) {
        g = { id, label, books: [] };
        groups.set(id, g);
      }
      g.books.push(b);
    };
    for (const b of books) {
      if (b.audience.storeIds.length === 0) put("all", ALL_STORES, b);
      else for (const sid of b.audience.storeIds) put(sid, storeName(sid), b);
    }
    const list = [...groups.values()];
    for (const g of list) {
      g.books.sort((a, b) => {
        if (sort === "count") return b.manualIds.length - a.manualIds.length;
        if (sort === "name") return a.title.localeCompare(b.title, "ja");
        return b.updatedAt - a.updatedAt; // recent
      });
    }
    // 全店共通 first, then stores in org order.
    const order = new Map(stores.map((s, i) => [s.id, i]));
    list.sort((a, b) => {
      if (a.id === "all") return -1;
      if (b.id === "all") return 1;
      return (order.get(a.id) ?? 9e9) - (order.get(b.id) ?? 9e9);
    });
    return list;
  }, [books, sort, stores, storeName]);

  return (
    <div className="bookshelf reveal d1">
      {shelves.map((g) => (
        <ShelfBand key={g.id} id={g.id} label={g.label} count={g.books.length}>
          {g.books.map((b) => (
            <BookCover
              key={b.bookId}
              model={coverModel(b, manualById, g.id === "all" ? ALL_STORES : g.label)}
              to={`/book/${b.bookId}`}
              spine={density === "spine"}
            />
          ))}
        </ShelfBand>
      ))}
    </div>
  );
}

/** One shelf band: header + a horizontally-scrollable rail with scrollLeft restore. */
function ShelfBand({
  id,
  label,
  count,
  children,
}: {
  id: string;
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const storageKey = `tebiki_shelf_scroll_${id}`;

  // Restore the rail's horizontal position when returning from an opened book.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const saved = Number(sessionStorage.getItem(storageKey) ?? "0");
    if (saved > 0) el.scrollLeft = saved;
  }, [storageKey]);

  function onScroll() {
    const el = railRef.current;
    if (el) sessionStorage.setItem(storageKey, String(Math.round(el.scrollLeft)));
  }

  return (
    <section className="shelf" aria-label={label}>
      <div className="shelf__head">
        <h2 className="shelf__name jp">{label}</h2>
        <span className="shelf__count">{count}冊</span>
      </div>
      <div className="shelf-rail" ref={railRef} onScroll={onScroll}>
        {children}
      </div>
    </section>
  );
}
